import { useState, type JSX } from 'react';
import { sessionEndReasonKey, type Session, type SessionKind } from '@netdeck/contracts';
import type { TFunction } from 'i18next';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';
import { StatusDot } from '../../shared/ui/StatusDot';
import { formatMoment } from '../../shared/format/time';
import { useSessions, useTerminateSession } from './api';
import { formatBytes } from '../../shared/format/bytes';

/**
 * Как называется вид сессии в списке.
 *
 * Столбец появился в фазе 9b: до неё в таблице были только сессии VNC,
 * и подписывать их было нечем. Терминал и видео стоят в ней рядом,
 * и различать их по имени машины — гадание.
 */
const KIND_LABEL_KEYS: Readonly<Record<SessionKind, string>> = {
  vnc: 'sessions.kind.vnc',
  ssh: 'sessions.kind.ssh',
  video_live: 'sessions.kind.videoLive',
  video_archive: 'sessions.kind.videoArchive',
};

/**
 * Активные сессии (§5.1, задача 13).
 *
 * Экран отвечает на один вопрос: кто сейчас сидит на чужих машинах.
 * Поэтому по умолчанию показываются работающие, а завершённые — переключателем:
 * история нужна реже и мешает увидеть текущее.
 */
export function SessionsPage({ canTerminateOthers }: { canTerminateOthers: boolean }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const [active, setActive] = useState(true);
  const sessions = useSessions(active);
  const terminate = useTerminateSession();

  const columns: Column<Session>[] = [
    {
      key: 'state',
      header: '',
      width: '24px',
      render: (row) => <StatusDot status={row.endedAt === null ? 'up' : 'unknown'} />,
    },
    {
      key: 'kind',
      header: t('notifications.column.kind'),
      width: '96px',
      sortValue: (row) => t(KIND_LABEL_KEYS[row.kind]),
      render: (row) => t(KIND_LABEL_KEYS[row.kind]),
    },
    {
      key: 'user',
      header: t('sessions.column.who'),
      width: '1.2fr',
      priority: 'summary',
      render: (row) => row.userDisplayName ?? t('common.dash'),
    },
    {
      key: 'node',
      header: t('sessions.column.machine'),
      width: '1.4fr',
      priority: 'title',
      render: (row) => (
        <span className="flex flex-col">
          <span>{row.nodeName ?? t('common.dash')}</span>
          <span className="text-xs text-dim">{row.siteName ?? ''}</span>
        </span>
      ),
    },
    {
      key: 'started',
      header: t('sessions.column.startedAt'),
      width: '140px',
      render: (row) => (
        <span className="font-mono text-xs">{formatMoment(row.startedAt, language)}</span>
      ),
    },
    {
      key: 'duration',
      header: t('sessions.column.duration'),
      width: '110px',
      priority: 'summary',
      render: (row) => <span className="font-mono text-xs">{duration(row, t)}</span>,
    },
    {
      key: 'traffic',
      header: t('sessions.column.traffic'),
      width: '110px',
      align: 'right',
      render: (row) => (
        <span className="font-mono text-xs" title={t('sessions.trafficHint')}>
          {formatBytes(row.bytesIn, t)} / {formatBytes(row.bytesOut, t)}
        </span>
      ),
    },
    {
      key: 'ip',
      header: t('sessions.column.from'),
      width: '130px',
      render: (row) => (
        <span className="font-mono text-xs">{row.clientIp ?? t('common.dash')}</span>
      ),
    },
    {
      key: 'action',
      header: '',
      width: '120px',
      priority: 'actions',
      align: 'right',
      render: (row) =>
        row.endedAt === null ? (
          <Button
            size="sm"
            variant="danger"
            disabled={terminate.isPending}
            onClick={() => terminate.mutate(row.id)}
          >
            {t('sessions.terminate')}
          </Button>
        ) : (
          <span className="text-xs text-dim">
            {endReason(row.endReason, t) ?? t('common.dash')}
          </span>
        ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      <Panel
        title={t('sessions.title')}
        subtitle={sessions.data ? `${String(sessions.data.items.length)}` : undefined}
        actions={
          <>
            <Button
              size="sm"
              variant={active ? 'primary' : 'ghost'}
              onClick={() => setActive(true)}
            >
              {t('sessions.activeOnly')}
            </Button>
            <Button
              size="sm"
              variant={active ? 'ghost' : 'primary'}
              onClick={() => setActive(false)}
            >
              {t('sessions.all')}
            </Button>
          </>
        }
      >
        {sessions.isPending ? <Loading /> : null}

        {sessions.error ? (
          <ErrorState title={t('sessions.listFailed')} hint={errorText(sessions.error)} />
        ) : null}

        {terminate.error ? (
          <ErrorState
            title={t('sessions.terminateFailed')}
            hint={
              canTerminateOthers ? errorText(terminate.error) : t('sessions.terminateForbidden')
            }
          />
        ) : null}

        {sessions.data ? (
          <DataTable
            columns={columns}
            rows={sessions.data.items}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title={active ? t('sessions.emptyActive') : t('sessions.emptyEver')}
                hint={t('sessions.emptyHint')}
              />
            }
          />
        ) : null}
      </Panel>
    </div>
  );
}

/**
 * Длительность сессии.
 *
 * У работающей считается от начала до сейчас: администратор смотрит сюда,
 * чтобы понять, идёт работа или кто-то забыл закрыть вкладку три часа назад.
 */
/** Причина закрытия сессии словами. Незнакомую причину показываем как есть. */
function endReason(reason: string | null, t: TFunction): string | null {
  const key = sessionEndReasonKey(reason);

  return key === null ? null : t(key, { defaultValue: key });
}

function duration(session: Session, t: TFunction): string {
  const from = new Date(session.startedAt).getTime();
  const to = session.endedAt === null ? Date.now() : new Date(session.endedAt).getTime();
  const seconds = Math.max(0, Math.round((to - from) / 1000));

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return t('overview.uptime.hours', { hours, minutes });
  }
  if (minutes > 0) {
    return t('overview.uptime.minutes', { count: minutes });
  }
  return t('overview.uptime.seconds', { count: seconds });
}
